import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Crown, Sparkles, Check, UserPlus, ArrowLeft } from 'lucide-react';
import { Dialog } from './Dialog.js';
import { Button } from './Button.js';
import { Badge } from './Badge.js';
import { usePlanStore } from '../../stores/planStore.js';
import { useAuthStore } from '../../stores/authStore.js';

const PLUS_BENEFITS = [
  'دانلود و ذخیره آفلاین نامحدود قطعات از بازارچه',
  'ساخت و مدیریت نامحدود روتین‌های تمرینی و بلوک‌ها',
  'ساخت نامحدود لیست‌های پخش با ظرفیت کامل',
  'ذخیره خودکار آخرین موقعیت و ادامه تمرین از همان نقطه',
  'دسترسی کامل به مترونوم حرفه‌ای، کسر میزان‌ها و صداهای متنوع',
  'ذخیره نامحدود کوک‌های دلخواه و شخصی‌سازی‌شده',
  'ثبت نامحدود درخواست تبلچر برای قطعات مورد علاقه'
];

export const UpgradeDialog: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { upgradeDialogOpen, limitInfo, closeUpgradeDialog } = usePlanStore();
  const { isGuest, isAuthenticated } = useAuthStore();

  const isGuestMode = isGuest || !isAuthenticated || limitInfo?.isGuestPrompt;

  const handleAuthRedirect = () => {
    closeUpgradeDialog();
    const currentPath = encodeURIComponent(location.pathname + location.search);
    navigate(`/auth?next=${currentPath}`);
  };

  return (
    <Dialog
      open={upgradeDialogOpen}
      onClose={closeUpgradeDialog}
      size="md"
      data-testid="upgrade-dialog"
    >
      <div className="p-6 flex flex-col gap-5 text-start">
        {/* Header with Icon and Badge */}
        <div className="flex items-start justify-between gap-3">
          <div className="flex items-center gap-3">
            <span
              className={`size-12 rounded-2xl flex items-center justify-center border shadow-inner ${
                isGuestMode
                  ? 'bg-primary-fill/15 border-primary/30 text-primary-soft'
                  : 'bg-accent/15 border-accent/30 text-accent-soft'
              }`}
            >
              {isGuestMode ? <UserPlus className="size-6" /> : <Crown className="size-6" />}
            </span>
            <div>
              <h2 className="text-lg font-bold text-base-content">
                {isGuestMode ? 'عضویت در فِرت‌بورد' : 'ارتقا به طرح پلاس'}
              </h2>
              <p className="text-xs text-base-content/60 mt-0.5">
                {isGuestMode
                  ? 'برای دسترسی به تمام امکانات، حساب رایگان خود را بسازید'
                  : 'دسترسی نامحدود و حرفه‌ای به تمام بخش‌ها'}
              </p>
            </div>
          </div>
          <Badge tone={isGuestMode ? 'info' : 'accent'} dot>
            {isGuestMode ? 'نوازنده مهمان' : 'طرح پلاس'}
          </Badge>
        </div>

        {/* Limit or Context Message */}
        {limitInfo?.message && (
          <div
            role="alert"
            data-testid="upgrade-limit-message"
            className="p-3.5 rounded-xl bg-base-300/60 border border-base-300 text-sm text-base-content leading-relaxed"
          >
            {limitInfo.message}
          </div>
        )}

        {/* Plus Feature Highlights */}
        <div className="flex flex-col gap-2 rounded-2xl bg-base-100/70 border border-base-300/80 p-4">
          <span className="text-xs font-semibold text-base-content/70 flex items-center gap-1.5 mb-1">
            <Sparkles className="size-3.5 text-accent-soft" />
            امکانات و مزایای طرح پلاس:
          </span>
          <div className="flex flex-col gap-2">
            {PLUS_BENEFITS.map((benefit, idx) => (
              <div key={idx} className="flex items-center gap-2.5 text-xs text-base-content/85">
                <span className="size-4.5 rounded-full bg-success/15 text-success-soft flex items-center justify-center shrink-0">
                  <Check className="size-3 stroke-[2.5]" />
                </span>
                <span>{benefit}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Contact Support note (Gateway pending in this phase) */}
        {!isGuestMode && (
          <p className="text-[11px] text-base-content/50 text-center leading-relaxed bg-base-200/50 p-2.5 rounded-xl border border-base-300/40">
            برای فعال‌سازی یا ارتقا به طرح پلاس، با مدیر سامانه یا پشتیبانی در ارتباط باشید. درگاه پرداخت آنلاین در فاز بعدی فعال خواهد شد.
          </p>
        )}

        {/* Actions */}
        <div className="flex items-center justify-end gap-3 pt-2 border-t border-base-300">
          {isGuestMode ? (
            <>
              <Button variant="ghost" size="md" onClick={closeUpgradeDialog}>
                شاید بعداً
              </Button>
              <Button
                variant="primary"
                size="md"
                leadingIcon={<UserPlus className="size-4" />}
                trailingIcon={<ArrowLeft className="size-4" />}
                onClick={handleAuthRedirect}
                data-testid="upgrade-auth-button"
              >
                ورود یا ایجاد حساب کاربری
              </Button>
            </>
          ) : (
            <Button
              variant="secondary"
              size="md"
              onClick={closeUpgradeDialog}
              data-testid="upgrade-close-button"
            >
              متوجه شدم
            </Button>
          )}
        </div>
      </div>
    </Dialog>
  );
};

UpgradeDialog.displayName = 'UpgradeDialog';
